import React, { useState, useEffect } from 'react';
import { authService } from '../../services/supabase';

const WaiterLogin = ({ onSuccess, onSwitchToManager }) => {
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRestaurantSelection, setShowRestaurantSelection] = useState(false);
  const [availableRestaurants, setAvailableRestaurants] = useState([]);
  const [pendingPassword, setPendingPassword] = useState('');

  // Add the useEffect here
  useEffect(() => {
    document.title = "Waiter Login - Menuron";
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!authService.isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) return;

  setLoading(true);
  try {
    const { data, error } = await authService.loginWaiter(
      formData.phone, 
      formData.password
    );
    
    if (error) {
      setErrors({ general: error.message });
      return;
    }

    // Check if waiter works at multiple restaurants
    if (data.multipleRestaurants) {
      setAvailableRestaurants(data.waiters);
      setPendingPassword(formData.password);
      setShowRestaurantSelection(true);
      return;
    }

    // Single restaurant - proceed with login
    onSuccess(data);
    
  } catch (error) {
    setErrors({ general: 'Login failed. Please try again.' });
  } finally {
    setLoading(false);
  }
};

  const handleRestaurantSelection = async (selectedWaiter) => {
    setLoading(true);
    try {
      // Create session for selected restaurant
      const { data, error } = await authService.createWaiterSession(selectedWaiter, pendingPassword);
      
      if (error) {
        setErrors({ general: error.message });
        return;
      }

      onSuccess(data);
    } catch (error) {
      setErrors({ general: 'Failed to login to selected restaurant.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
        border: '1px solid rgba(255, 68, 68, 0.1)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img 
            src="https://i.ibb.co/hF3Q8YrX/menuron-high-resolution-logo.png" 
            alt="Menuron" 
            style={{ height: '140px', marginBottom: '20px', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}
          />
          <h1 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '1.8rem', 
            fontWeight: '700',
            background: 'linear-gradient(135deg, #28a745, #20c997)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Waiter Login
          </h1>
          <p style={{ margin: '0', color: '#666', fontSize: '1rem' }}>
            Access your restaurant's order system
          </p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '15px 18px',
                border: errors.phone ? '2px solid #dc2626' : '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#28a745'}
              onBlur={(e) => e.target.style.borderColor = errors.phone ? '#dc2626' : '#e5e7eb'}
            />
            {errors.phone && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                {errors.phone}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '15px 45px 15px 18px',
                  border: errors.password ? '2px solid #dc2626' : '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#28a745'}
                onBlur={(e) => e.target.style.borderColor = errors.password ? '#dc2626' : '#e5e7eb'}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {errors.password && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                {errors.password}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #28a745, #20c997)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '15px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(40, 167, 69, 0.3)',
              transition: 'all 0.3s ease',
              marginBottom: '20px'
            }}
          >
            {loading ? 'Logging in...' : 'Login to Orders'}
          </button>

          {/* Navigation Link */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={onSwitchToManager}
              style={{
                background: 'none',
                border: '2px solid #FF6B00',
                color: '#FF6B00',
                borderRadius: '25px',
                padding: '8px 20px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#FF6B00';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#FF6B00';
              }}
            >
              Login as Manager
            </button>
          </div>
        </form>
      </div>

      {/* Restaurant Selection Modal - ADD THIS ENTIRE BLOCK HERE */}
      {showRestaurantSelection && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', textAlign: 'center' }}>
              Select Restaurant
            </h3>
            <p style={{ margin: '0 0 20px 0', textAlign: 'center', color: '#666' }}>
              You work at multiple restaurants. Please select which one to log into:
            </p>
            
            <div style={{ display: 'grid', gap: '10px' }}>
              {availableRestaurants.map(waiter => (
                <button
                  key={waiter.id}
                  onClick={() => handleRestaurantSelection(waiter)}
                  disabled={loading}
                  style={{
                    padding: '15px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  {waiter.restaurants.name}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setShowRestaurantSelection(false)}
              style={{
                width: '100%',
                marginTop: '15px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                background: 'transparent',
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default WaiterLogin;
